import type { BackendProduct, Part, ProductOption, ProductOptions } from '../types';

export type ProductSelectionKey = 'color' | 'storage' | 'condition' | 'battery' | 'warranty' | 'sim' | 'quality';
export type ProductSelections = Record<ProductSelectionKey, string>;

export const PRODUCT_OPTION_BINDINGS: ReadonlyArray<{
    selection: ProductSelectionKey;
    family: keyof ProductOptions;
    label: string;
}> = [
    { selection: 'color', family: 'colors', label: 'Couleur' },
    { selection: 'storage', family: 'storage', label: 'Stockage' },
    { selection: 'condition', family: 'conditions', label: 'État esthétique' },
    { selection: 'battery', family: 'battery', label: 'Batterie' },
    { selection: 'warranty', family: 'warranty', label: 'Garantie' },
    { selection: 'sim', family: 'sim', label: 'Type de SIM' },
    { selection: 'quality', family: 'quality', label: 'Qualité / Grade' }
];

const cleanText = (value: unknown): string => typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();

export const normalizeProductOptions = (raw: BackendProduct['options']): ProductOptions => {
    let parsed: unknown = raw;
    if (typeof raw === 'string') {
        try { parsed = JSON.parse(raw); } catch { parsed = {}; }
    }
    const record = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
    return Object.fromEntries(PRODUCT_OPTION_BINDINGS.map(({ family }) => {
        const list = Array.isArray(record[family]) ? record[family] as Array<Record<string, unknown>> : [];
        const options = list.map((option): ProductOption | null => {
            const id = cleanText(option.id);
            const name = cleanText(option.name);
            if (!id || !name) return null;
            const priceDiff = Number(option.priceDiff);
            return { id, name, priceDiff: Number.isFinite(priceDiff) ? priceDiff : 0, ...(cleanText(option.colorCode) ? { colorCode: cleanText(option.colorCode) } : {}) };
        }).filter((option): option is ProductOption => option !== null);
        return [family, options];
    })) as ProductOptions;
};

export const mapPublicProductCharacteristics = (product: BackendProduct): Pick<Part,
    'condition' | 'quality' | 'color' | 'storage' | 'batteryHealth' | 'warranty' | 'sim' | 'camera' | 'options'
> => ({
    condition: cleanText(product.condition),
    quality: cleanText(product.quality),
    color: cleanText(product.color),
    storage: cleanText(product.storage),
    batteryHealth: cleanText(product.batteryHealth ?? product.battery_health),
    warranty: cleanText(product.warranty),
    sim: cleanText(product.sim),
    camera: cleanText(product.camera),
    options: normalizeProductOptions(product.options)
});

export const initialProductSelections = (options?: ProductOptions): ProductSelections => Object.fromEntries(
    PRODUCT_OPTION_BINDINGS.map(({ selection, family }) => [selection, options?.[family]?.[0]?.id || ''])
) as ProductSelections;

export const selectedProductOptions = (options: ProductOptions | undefined, selections: ProductSelections): Partial<Record<ProductSelectionKey, ProductOption>> => Object.fromEntries(
    PRODUCT_OPTION_BINDINGS.map(({ selection, family }) => [selection, options?.[family]?.find((option) => option.id === selections[selection])])
) as Partial<Record<ProductSelectionKey, ProductOption>>;

export const productCharacteristicRows = (product: Part, selections: ProductSelections): Array<{ key: string; label: string; value: string }> => {
    const selected = selectedProductOptions(product.options, selections);
    const rows = [
        { key: 'condition', label: 'État du produit', value: selected.condition?.name || cleanText(product.condition) },
        { key: 'quality', label: 'Qualité / Grade', value: selected.quality?.name || cleanText(product.quality) },
        { key: 'color', label: 'Couleur', value: selected.color?.name || cleanText(product.color) },
        { key: 'storage', label: 'Stockage', value: selected.storage?.name || cleanText(product.storage) },
        { key: 'battery', label: 'État de la batterie', value: selected.battery?.name || cleanText(product.batteryHealth) },
        { key: 'warranty', label: 'Garantie', value: selected.warranty?.name || cleanText(product.warranty) },
        { key: 'sim', label: 'Type de SIM', value: selected.sim?.name || cleanText(product.sim) },
        { key: 'camera', label: 'Appareil photo', value: cleanText(product.camera) }
    ];
    return rows.filter((row) => row.value);
};

export const productIdentitySummaryFromRows = (rows: Array<{ key: string; value: string }>): string => {
    const priority = ['storage', 'condition', 'quality', 'color', 'battery'];
    return priority
        .map((key) => rows.find((row) => row.key === key)?.value || '')
        .filter(Boolean)
        .slice(0, 4)
        .join(' · ');
};

export const productIdentitySummary = (product: Part): string => productIdentitySummaryFromRows(
    productCharacteristicRows(product, initialProductSelections(product.options))
);

export const productAvailabilityLabel = (product: Pick<Part, 'stock' | 'price'>): string => {
    if (Number(product.stock) > 0) return `${Number(product.stock)} en stock`;
    return Number(product.price) > 0 ? 'Sur commande' : 'Indisponible';
};

export const formatProductPrice = (amount: unknown, currencyCode?: string): string => {
    const price = Number(amount);
    const currency = cleanText(currencyCode).toUpperCase();
    if (!(price > 0) || !currency) return 'Sur demande';
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
    } catch {
        return `${price.toFixed(2)} ${currency}`;
    }
};
